import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { MetricConfig } from '../types';
import { getSupabaseToken } from './supabaseAuth';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase configuration. Please check your environment variables.');
}

// The anon key is public, so it grants nothing on its own. Every request carries
// an exchanged token whose facility_id / user_role claims drive the RLS policies.
// supabase-js invokes this callback per request, so callers need no changes and
// concurrent queries share one in-flight exchange.
export const supabase: SupabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
  accessToken: async () => getSupabaseToken(),
});

// Database service for metric configurations
export class MetricConfigService {
  /**
   * Get effective metric configurations, optionally for a facility.
   * New model: single table with facility_id, unique (metric_key, facility_id)
   * Strategy: select rows where facility_id is null OR equals facilityId.
   * Prefer facility-specific row over global for the same metric_key.
   */
  static async getMetricConfigs(facilityId?: string): Promise<MetricConfig[]> {
    let query = supabase
      .from('metric_configs')
      .select('*');

    if (facilityId && typeof facilityId === 'string' && facilityId.trim().length > 0) {
      // Get facility-specific configs AND global configs (facility_id is null)
      query = query.or(`facility_id.is.null,facility_id.eq.${facilityId}`);
    } else {
      // No facility ID provided - only return global configs
      query = query.is('facility_id', null);
    }

    const { data, error } = await query.order('sort_order', { ascending: true });

    if (error) {
      console.error('Error fetching metric configs:', error);
      throw error;
    }

    const rows = (data || []);
    // Deduplicate by metric_key preferring facility row
    if (facilityId && typeof facilityId === 'string' && facilityId.trim().length > 0) {
      const byKey = new Map<string, any>();
      // Put globals first, then overwrite with facility rows
      rows
        .sort((a, b) => (a.facility_id ? 1 : 0) - (b.facility_id ? 1 : 0))
        .forEach(r => byKey.set(r.metric_key, r));
      return Array.from(byKey.values()).map(this.mapDbToMetricConfig);
    }

    return rows.map(this.mapDbToMetricConfig);
  }

  // facility_metric_configs no longer used in the new single-table model

  /**
   * Get effective metric configuration (facility override or default)
   */
  static async getEffectiveMetricConfigs(facilityId?: string): Promise<MetricConfig[]> {
    return this.getMetricConfigs(facilityId);
  }

  /**
   * Create new metric configuration
   */
  static async createMetricConfig(config: Omit<MetricConfig, 'id'>, facilityId?: string): Promise<MetricConfig> {
    const { data, error } = await supabase
      .from('metric_configs')
      .insert({
        metric_key: config.metricKey,
        display_name: config.displayName,
        category_id: config.categoryId, // UUID reference
        tab: config.tab, // Legacy field (optional)
        target_value: config.targetValue ?? null,
        target_min: config.targetMin ?? null,
        target_max: config.targetMax ?? null,
        green_min: config.greenMin,
        green_max: config.greenMax,
        yellow_min: config.yellowMin,
        yellow_max: config.yellowMax,
        red_min: config.redMin,
        red_max: config.redMax,
        units: config.units,
        visualization_type: config.visualizationType,
        is_visible: config.isVisible,
        sort_order: config.sortOrder,
        green_label: config.greenLabel,
        yellow_label: config.yellowLabel,
        red_label: config.redLabel,
        help_text: config.helpText,
        dimension_id: config.dimensionId || null,
        self_assessment_prompt: config.selfAssessmentPrompt || null,
        facility_id: facilityId || null,
        display_as_0_to_100: config.displayAs0to100 ?? false,
        caredaily_value_key: config.careDailyValueKey || null,
        caredaily_display_key: config.careDailyDisplayKey || null,
        show_inline_note: config.showInlineNote ?? false,
        use_custom_color: config.useCustomColor ?? false,
        custom_color: config.customColor || null,
        use_caredaily_norms: config.useCareDailyNorms ?? true,
        band_color_above_average: config.bandColorAboveAverage || null,
        band_color_normal: config.bandColorNormal || null,
        band_color_below_average: config.bandColorBelowAverage || null
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating metric config:', error);
      throw error;
    }

    return this.mapDbToMetricConfig(data);
  }

  /**
   * Update existing metric configuration
   */
  static async updateMetricConfig(id: string, config: Omit<MetricConfig, 'id'>, facilityId?: string): Promise<MetricConfig> {
    const { data, error } = await supabase
      .from('metric_configs')
      .update({
        metric_key: config.metricKey,
        display_name: config.displayName,
        category_id: config.categoryId, // UUID reference
        tab: config.tab, // Legacy field (optional)
        target_value: config.targetValue ?? null,
        target_min: config.targetMin ?? null,
        target_max: config.targetMax ?? null,
        green_min: config.greenMin,
        green_max: config.greenMax,
        yellow_min: config.yellowMin,
        yellow_max: config.yellowMax,
        red_min: config.redMin,
        red_max: config.redMax,
        units: config.units,
        visualization_type: config.visualizationType,
        is_visible: config.isVisible,
        sort_order: config.sortOrder,
        green_label: config.greenLabel,
        yellow_label: config.yellowLabel,
        red_label: config.redLabel,
        help_text: config.helpText,
        dimension_id: config.dimensionId || null,
        self_assessment_prompt: config.selfAssessmentPrompt || null,
        facility_id: facilityId || null,
        display_as_0_to_100: config.displayAs0to100 ?? false,
        caredaily_value_key: config.careDailyValueKey || null,
        caredaily_display_key: config.careDailyDisplayKey || null,
        show_inline_note: config.showInlineNote ?? false,
        use_custom_color: config.useCustomColor ?? false,
        custom_color: config.customColor || null,
        use_caredaily_norms: config.useCareDailyNorms ?? true,
        band_color_above_average: config.bandColorAboveAverage || null,
        band_color_normal: config.bandColorNormal || null,
        band_color_below_average: config.bandColorBelowAverage || null,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating metric config:', error);
      throw error;
    }

    return this.mapDbToMetricConfig(data);
  }

  /**
   * Create or update metric configuration (legacy method for backward compatibility)
   */
  static async upsertMetricConfig(config: Omit<MetricConfig, 'id'>, facilityId?: string): Promise<MetricConfig> {
    // Check if a record already exists for this metric_key + facility combo
    let query = supabase
      .from('metric_configs')
      .select('id')
      .eq('metric_key', config.metricKey);

    if (facilityId) {
      query = query.eq('facility_id', facilityId);
    } else {
      query = query.is('facility_id', null);
    }

    const { data: existing } = await query.maybeSingle();

    if (existing?.id) {
      return this.updateMetricConfig(existing.id, config, facilityId);
    }
    return this.createMetricConfig(config, facilityId);
  }

  /**
   * Create or update facility-specific metric configuration
   */
  // Facility overrides table is deprecated in this model

  /**
   * Delete metric configuration
   */
  static async deleteMetricConfig(id: string): Promise<void> {
    const { error } = await supabase
      .from('metric_configs')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting metric config:', error);
      throw error;
    }
  }

  /**
   * Delete facility metric configuration override
   */
  static async deleteFacilityMetricConfig(facilityId: string, metricConfigId: string): Promise<void> {
    const { error } = await supabase
      .from('facility_metric_configs')
      .delete()
      .eq('facility_id', facilityId)
      .eq('metric_config_id', metricConfigId);

    if (error) {
      console.error('Error deleting facility metric config:', error);
      throw error;
    }
  }

  /**
   * Map database row to MetricConfig type
   */
  private static mapDbToMetricConfig(row: any): MetricConfig {
    // Development-only detailed logging
    if (process.env.NODE_ENV !== 'production') {
      console.log('🔍 Mapping database row to MetricConfig:', {
        id: row.id,
        metric_key: row.metric_key,
        display_name: row.display_name,
        category_id: row.category_id,
        tab: row.tab, // Legacy field
        is_visible: row.is_visible,
        facility_id: row.facility_id
      });
    }
    
    const mapped = {
      id: row.id || '',
      metricKey: row.metric_key || '',
      displayName: row.display_name || '',
      categoryId: row.category_id || '', // UUID reference to dashboard_categories
      tab: row.tab, // Legacy field for backward compatibility
      targetValue: row.target_value,
      targetMin: row.target_min,
      targetMax: row.target_max,
      greenMin: row.green_min,
      greenMax: row.green_max,
      yellowMin: row.yellow_min,
      yellowMax: row.yellow_max,
      redMin: row.red_min,
      redMax: row.red_max,
      units: row.units || '',
      visualizationType: row.visualization_type || 'data_card',
      isVisible: row.is_visible !== false, // Default to true if null/undefined
      sortOrder: row.sort_order || 0,
      greenLabel: row.green_label || '',
      yellowLabel: row.yellow_label || '',
      redLabel: row.red_label || '',
      helpText: row.help_text || '',
      dimensionId: row.dimension_id || undefined,
      selfAssessmentPrompt: row.self_assessment_prompt || undefined,
      displayAs0to100: row.display_as_0_to_100 === true,
      careDailyValueKey: row.caredaily_value_key || null,
      careDailyDisplayKey: row.caredaily_display_key || null,
      showInlineNote: row.show_inline_note === true,
      useCustomColor: row.use_custom_color === true,
      customColor: row.custom_color || null,
      useCareDailyNorms: row.use_caredaily_norms !== false, // default true, including for pre-migration rows
      bandColorAboveAverage: row.band_color_above_average ?? null,
      bandColorNormal: row.band_color_normal ?? null,
      bandColorBelowAverage: row.band_color_below_average ?? null
    };
    
    // Development-only mapping confirmation
    if (process.env.NODE_ENV !== 'production') {
      console.log('✅ Mapped to MetricConfig:', { 
        id: mapped.id, 
        metricKey: mapped.metricKey, 
        displayName: mapped.displayName,
        categoryId: mapped.categoryId,
        tab: mapped.tab, // Legacy
        isVisible: mapped.isVisible 
      });
    }
    
    return mapped;
  }

  // Discovered metrics functionality removed - using SimpleDynamicMetrics instead

  // Initialize defaults functionality removed - use dynamic metric discovery instead
}

// Permissions service for admin access gating
export class PermissionService {
  static async isAdmin(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('user_permissions')
        .select('permission')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error checking user permission:', error);
        return false;
      }
      return (data?.permission === 'admin');
    } catch (e) {
      console.error('Permission check failed:', e);
      return false;
    }
  }
}
